/**
 * @description      : Class-based Workflow Summary page, as ported in from initial rewrite.
 * @author           : jmcait
 * @group            :
 * @created          : 15/10/2024 - 13:02:25
 **/
import * as React from "react";
import { DataAccess, IParams } from "../../data/DataAccess";
import { Logging, LoggingLevel } from "../../Logging";
import { IItem } from "../../models/IItem";
import DetailPanel from "./DetailPanel";
import DiagnosticPanel, { RawEventDriver } from "./DiagnosticPanel";
import TaskListsPanel from "./TaskListsPanel";
import Timeline from "./Timeline";
import { useEffect, useState } from "react";
import { useUser } from "../../Providers/UserProvider";
import { TogglesClass } from "./Toggles";
import IfPermitted from "../../components/IfPermitted";

interface CWSProps {
  MODE_LOCAL: boolean;
  MODE_DEVELOPMENT: boolean;
  MODE_STAGING: boolean;
  MODE_LIVE: boolean;
  Logger: Logging;
  da: DataAccess;
  figuid: string;
  wiguid: string;
  Context: "Standard" | "Audit" | "Admin";
}

interface RelevantGroup {
  GroupID: number;
  GroupName: string;
}

export default function CollagenWorkflowSummary(props: CWSProps) {
  const user = useUser();

  const [FormInstanceID, SetFormInstanceID] = useState(0);
  const [ProvisionalReassign, SetProvisionalReassign] = useState("");
  const [ProvisionalReassignGroup, SetProvisionalReassignGroup] = useState("");
  const [RelevantGroups, SetRelevantGroups] = useState<Array<RelevantGroup>>(
    [],
  );

  const initToggles = new TogglesClass();

  if (props.Context === "Admin") {
    initToggles.SortTimeDescending = true;
    initToggles.ShowAccessLocks = true;
    initToggles.ShowMail = true;
    initToggles.ShowData = true;
    initToggles.ShowAssignments = true;
    initToggles.ShowEvents = true;
    initToggles.ShowDesignations = true;
    initToggles.ShowOther = true;
    initToggles.ShowMilliseconds = true;
  } else if (props.Context === "Audit") {
    initToggles.SortTimeDescending = true;
    initToggles.ShowAccessLocks = false;
    initToggles.ShowMail = true;
    initToggles.ShowData = true;
    initToggles.ShowAssignments = true;
    initToggles.ShowEvents = true;
    initToggles.ShowDesignations = false;
    initToggles.ShowOther = false;
    initToggles.ShowMilliseconds = false;
  } else if (props.Context === "Standard") {
    initToggles.SortTimeDescending = true;
    initToggles.ShowAccessLocks = false;
    initToggles.ShowMail = true;
    initToggles.ShowData = true;
    initToggles.ShowAssignments = true;
    initToggles.ShowEvents = true;
    initToggles.ShowDesignations = false;
    initToggles.ShowOther = false;
    initToggles.ShowMilliseconds = false;
  }

  const [message, setMessage] = useState("");
  const [ValidFormInstance, SetValidFormInstance] = useState<
    boolean | undefined
  >(undefined);
  const [Toggles, SetToggles] = useState<TogglesClass>(initToggles);
  const [DetailTable, SetDetailTable] = useState<Array<IItem>>([]);
  const [AnalysisTable, SetAnalysisTable] = useState<Array<IItem>>([]);
  const [CommitTable, SetCommitTable] = useState<Array<IItem>>([]);
  const [CommitDatumsTable, SetCommitDatumsTable] = useState<Array<IItem>>([]);
  const [AssignmentHistoryTable, SetAssignmentHistoryTable] = useState<
    Array<IItem>
  >([]);
  const [EventTable, SetEventTable] = useState<Array<IItem>>([]);
  const [DesignationTable, SetDesignationTable] = useState<Array<IItem>>([]);
  const [AccessTable, SetAccessTable] = useState<Array<IItem>>([]);
  const [OtherTable, SetOtherTable] = useState<Array<IItem>>([]);
  const [MailTable, SetMailTable] = useState<Array<IItem>>([]);
  const [TaskListsTable, SetTaskListsTable] = useState<Array<IItem>>([]);

  // scanner data
  const [LastChecked, SetLastChecked] = useState<Date>();
  const [DatumHistoryCount, SetDatumHistoryCount] = useState<Number>(0);
  const [AssignmentHistoryCount, SetAssignmentHistoryCount] =
    useState<Number>(0);
  const [EventCount, SetEventCount] = useState<Number>(0);
  const [DesignationCount, SetDesignationCount] = useState<Number>(0);
  const [AccessCount, SetAccessCount] = useState<Number>(0);
  const [MailRequestCount, SetMailRequestCount] = useState<Number>(0);
  const [EventChecksum, SetEventChecksum] = useState<Number>(0);
  const [DesignationChecksum, SetDesignationChecksum] = useState<Number>(0);
  const [AccessChecksum, SetAccessChecksum] = useState<Number>(0);
  const [OtherChecksum, SetOtherChecksum] = useState<Number>(0);
  const [DetailChecksum, SetDetailChecksum] = useState<Number>(0);
  const [TaskListsChecksum, SetTaskListsChecksum] = useState<Number>(0);
  const [MailRequestChecksum, SetMailRequestChecksum] = useState<Number>(0);
  const [DiagnosisRequestOptions, SetDiagnosisRequestOptions] = useState<
    Array<{ Name: string; Value: string }>
  >([]);

  const [DiagnoseThis, SetDiagnoseThis] = useState<string>();
  const [RequestedDiagnosis, SetRequestedDiagnosis] = useState<number>();
  const [ForcedEventID, SetForcedEventID] = useState<number>();
  const [Diagnosis, SetDiagnosis] = useState<IItem[]>();
  const [SprocDataTable, SetSprocDataTable] = useState<IItem[]>();
  const [InstanceDataTable, SetInstanceDataTable] = useState<IItem[]>();
  const [DiagnosisObject, SetDiagnosisObject] = useState<RawEventDriver[]>();

  useEffect(() => {
    let _newFullDiag: RawEventDriver[] = [];
    if (Diagnosis !== undefined) {
      if (Diagnosis.length > 0) {
        Diagnosis.forEach((th) => {
          _newFullDiag.push({
            AssignedInLastMinute: th["AssignedInLastMinute"],
            AssignmentDetectionSQL: th["AssignmentDetectionSQL"],
            ChangedInLastMinute: th["ChangedInLastMinute"],
            Comparatee_DataColumn: th["Comparatee_DataColumn"],
            Comparatee_UIComponentID: th["Comparatee_UIComponentID"],
            Comparatee_Value: th["Comparatee_Value"],
            Comparatee_Variable: th["Comparatee_Variable"],
            Comparator: th["Comparator"],
            DataColumn: th["DataColumn"],
            EventDriverID: th["EventDriverID"],
            LeftEventDriverID: th["LeftEventDriverID"],
            RightEventDriverID: th["RightEventDriverID"],
            ScheduleID: th["ScheduleID"],
            ScratchDefinition: th["ScratchDefinition"],
            SlipstreamEventID: th["SlipstreamEventID"],
            UIComponentID: th["UIComponentID"],
            Level: th["Level"],
            Path: th["Path"],
            _AND: th["_AND"],
            _OR: th["_OR"],
          });
        });
        SetDiagnosisObject(_newFullDiag);
      }
    }
  }, [Diagnosis]);

  const refreshInterval = 60000;

  // once on load only
  useEffect(() => {
    pollSummarySprocs();
    setInterval(() => {
      pollSummarySprocs();
    }, refreshInterval);
  }, []);

  useEffect(() => {
    if (FormInstanceID != 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getrelevantgroups",
          params: ["@FIID", FormInstanceID.toString()],
        },
        SetRelevantGroups,
      );
    }
  }, [FormInstanceID]);

  useEffect(() => {
    if (DatumHistoryCount !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getdatumhistory",
          params: ["@FIGUID", props.figuid],
        },
        SetCommitTable,
        SetCommitDatumsTable,
      );
    }
  }, [DatumHistoryCount]);

  useEffect(() => {
    if (AssignmentHistoryCount !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getassignmenthistory",
          params: ["@FIGUID", props.figuid],
        },
        SetAssignmentHistoryTable,
      );
    }
  }, [AssignmentHistoryCount]);

  useEffect(() => {
    if (DetailChecksum !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getanalysistable",
          params: ["@FIGUID", props.figuid],
        },
        SetAnalysisTable,
      );

      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getdetailtable",
          params: ["@FIGUID", props.figuid],
        },
        SetDetailTable,
      );
    }
  }, [DetailChecksum]);

  useEffect(() => {
    props.da
      .get(
        "/api/sproc",
        JSON.stringify({
          sproc: "sputilities.webui.SUMMARY_getrelatedevents",
          params: ["@FIGUID", props.figuid],
        }),
      )
      .then((data) => {
        const Data0 = data === undefined ? [] : data[0];

        if (Data0 !== undefined) {
          var _diagnosisRequestOptions: { Name: string; Value: string }[] = [];
          _diagnosisRequestOptions.push({
            Value: "-",
            Name: "-",
          });
          Data0.forEach((th: any) => {
            _diagnosisRequestOptions.push({
              Value: th.EventID,
              Name: th.EventName,
            });
          });
          SetDiagnosisRequestOptions(_diagnosisRequestOptions);
        }
      });

    props.da
      .get(
        "/api/sproc",
        JSON.stringify({
          sproc: "sputilities.webui.SUMMARY_getforminstanceid",
          params: ["@FIGUID", props.figuid],
        }),
      )
      .then((data) => {
        const Data0 = data === undefined ? [] : data[0][0];
        SetFormInstanceID(Number.parseInt(Data0.forminstanceid));
      });
  }, [props.figuid]);

  useEffect(() => {
    if (EventChecksum !== 0 && EventCount !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_geteventqueue",
          params: ["@FIGUID", props.figuid],
        },
        SetEventTable,
      );
    }
  }, [EventChecksum, EventCount]);

  useEffect(() => {
    if (DesignationChecksum !== 0 && DesignationCount !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getdesignationqueue",
          params: ["@FIGUID", props.figuid],
        },
        SetDesignationTable,
      );
    }
  }, [DesignationChecksum, DesignationCount]);

  useEffect(() => {
    if (TaskListsChecksum !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_gettasklistpresences",
          params: ["@FIGUID", props.figuid],
        },
        SetTaskListsTable,
      );
    }
  }, [TaskListsChecksum]);

  useEffect(() => {
    if (OtherChecksum !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getotherentries",
          params: ["@FIGUID", props.figuid],
        },
        SetOtherTable,
      );
    }
  }, [OtherChecksum]);

  useEffect(() => {
    if (AccessChecksum !== 0 && AccessCount !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getaccessentries",
          params: ["@FIGUID", props.figuid],
        },
        SetAccessTable,
      );
    }
  }, [AccessChecksum, AccessCount]);

  useEffect(() => {
    if (MailRequestChecksum !== 0 && MailRequestCount !== 0) {
      getData(
        {
          sproc: "sputilities.webui.SUMMARY_getmailrequests",
          params: ["@FIGUID", props.figuid],
        },
        SetMailTable,
      );
    }
  }, [MailRequestChecksum, MailRequestCount]);

  useEffect(() => {
    if (
      RequestedDiagnosis !== undefined &&
      RequestedDiagnosis !== 0 &&
      FormInstanceID !== 0
    ) {
      getDiagnosis(FormInstanceID, RequestedDiagnosis);
    }
  }, [RequestedDiagnosis, FormInstanceID]);

  useEffect(() => {
    if (
      ForcedEventID !== undefined &&
      ForcedEventID !== 0 &&
      FormInstanceID !== 0
    ) {
      forceEventRun(FormInstanceID, ForcedEventID);
    }
  }, [ForcedEventID, FormInstanceID]);

  const ForceReassign = () => {
    getData({
      sproc: "sputilities.webui.SUMMARY_ForceReassign",
      params: [
        "@FIID",
        FormInstanceID.toString(),
        "@ReassignTo",
        ProvisionalReassign !== ""
          ? ProvisionalReassign
          : ProvisionalReassignGroup,
      ],
    });

    SetProvisionalReassign("");
    SetProvisionalReassignGroup("");
  };

  const Designate = () => {
    getData({
      sproc: "sputilities.webui.SUMMARY_queuenewdesignation",
      params: ["@FIGUID", props.figuid],
    });
  };

  const forceEventRun = (FormInstanceID: number, EventID: number) => {
    getData(
      {
        sproc: "sputilities.webui.SUMMARY_QueueNewEvent",
        params: ["@FIGUID", props.figuid, "@EID", EventID.toString()],
      },
      Designate,
    );
  };

  const getDiagnosis = (FormInstanceID: number, EventID: number) => {
    getData(
      {
        sproc: "sputilities.webui.SUMMARY_getdiagnosis",
        params: [
          "@FIID",
          FormInstanceID.toString(),
          "@EventID",
          EventID.toString(),
        ],
      },
      SetDiagnosis,
    );

    getData(
      {
        sproc: "sputilities.webui.SUMMARY_getdiagnosissprocdata",
        params: [
          "@FIID",
          FormInstanceID.toString(),
          "@EventID",
          EventID.toString(),
        ],
      },
      SetSprocDataTable,
    );

    getData(
      {
        sproc: "sputilities.webui.SUMMARY_getdiagnosisinstancedata",
        params: [
          "@FIID",
          FormInstanceID.toString(),
          "@EventID",
          EventID.toString(),
        ],
      },
      SetInstanceDataTable,
    );
  };

  const pollSummarySprocs = () => {
    props.da
      .get(
        "/api/sproc",
        JSON.stringify({
          sproc: "sputilities.webui.SUMMARY_getupdatemeta",
          params: ["@FIGUID", props.figuid],
        }),
      )
      .then((data: any) => {
        if (data !== undefined && data[0] !== undefined) {
          props.Logger.log("pollSummarySprocs has run.");

          SetLastChecked(data[0][0].LastChecked as Date);
          SetDatumHistoryCount(Number.parseInt(data[0][0].DatumHistoryCount));
          SetAssignmentHistoryCount(
            Number.parseInt(data[0][0].AssignmentHistoryCount),
          );
          SetEventCount(Number.parseInt(data[0][0].EventCount));
          SetDesignationCount(Number.parseInt(data[0][0].DesignationCount));
          SetAccessCount(Number.parseInt(data[0][0].AccessCount));
          SetMailRequestCount(Number.parseInt(data[0][0].MailRequestCount));
          SetEventChecksum(Number.parseInt(data[0][0].LatestEventChecksum));
          SetDesignationChecksum(
            Number.parseInt(data[0][0].LatestDesignationChecksum),
          );
          SetOtherChecksum(Number.parseInt(data[0][0].LatestOtherChecksum));
          SetMailRequestChecksum(
            Number.parseInt(data[0][0].LatestMailRequestChecksum),
          );
          SetDetailChecksum(Number.parseInt(data[0][0].LatestDetailChecksum));
          SetAccessChecksum(Number.parseInt(data[0][0].LatestAccessChecksum));
          SetTaskListsChecksum(Number.parseInt(data[0][0].TaskListsChecksum));
          SetValidFormInstance(data[0][0].ValidFormInstance === "1");
        }
      });
  };

  const getData = (
    params: IParams,
    method?: (table: any) => void,
    method2?: (table: any) => void,
  ) => {
    props.da.get("/api/sproc", JSON.stringify(params)).then(
      (data) => {
        const Data0 = data === undefined ? [] : data[0];
        const Data1 = data === undefined ? [] : data[1];

        if (
          (data?.length ?? 0) === 1 &&
          Data0.length === 1 &&
          Data0[0]["ERROR"]
        ) {
          setMessage(params.sproc + " :: " + Data0[0]["ERROR"]);
        } else {
          if (method !== undefined) {
            method(Data0);
          }
          if (method2 !== undefined) {
            method2(Data1);
          }
        }
      },
      (reason) => {
        setMessage(reason);
      },
    );
  };

  const Toggle = (ToggleName: string) => {
    var _toggles: TogglesClass = { ...Toggles };

    if (ToggleName === "SortTimeDescending") {
      _toggles.SortTimeDescending = !Toggles.SortTimeDescending;
    }
    if (ToggleName === "ShowAccessLocks") {
      _toggles.ShowAccessLocks = !Toggles.ShowAccessLocks;
    }
    if (ToggleName === "ShowMail") {
      _toggles.ShowMail = !Toggles.ShowMail;
    }
    if (ToggleName === "ShowData") {
      _toggles.ShowData = !Toggles.ShowData;
    }
    if (ToggleName === "ShowAssignments") {
      _toggles.ShowAssignments = !Toggles.ShowAssignments;
    }
    if (ToggleName === "ShowEvents") {
      _toggles.ShowEvents = !Toggles.ShowEvents;
    }
    if (ToggleName === "ShowDesignations") {
      _toggles.ShowDesignations = !Toggles.ShowDesignations;
    }
    if (ToggleName === "ShowOther") {
      _toggles.ShowOther = !Toggles.ShowOther;
    }
    if (ToggleName === "ShowMilliseconds") {
      _toggles.ShowMilliseconds = !Toggles.ShowMilliseconds;
    }
    SetToggles(_toggles);
  };

  const PassthroughRequeue = (EID: Number, FIGUID: string) => {
    props.Logger.log("PassthroughRequeue start", LoggingLevel.INFO);

    getData({
      sproc: "sputilities.webui.SUMMARY_QueueNewEvent",
      params: ["@FIGUID", FIGUID.toString(), "@EID", EID.toString()],
    });

    // merge the returned event into the grid?
  };

  // RENDER:
  if (message !== "") {
    return <div className="error-message">{message}</div>;
  } else if (ValidFormInstance === false) {
    return (
      <div className="error-message">
        The specified Form Instance doesn't exist.
      </div>
    );
  } else if (ValidFormInstance === undefined) {
    return (
      <div id="ECF-None">
        <h1>Workflow Summary</h1>
        <div className="loading" title="Loading..." />
      </div>
    );
  } else if (ValidFormInstance === true) {
    return (
      <div id="WFSumm">
        <h1>Workflow Summary</h1>
        <DetailPanel
          DetailTable={DetailTable}
          AnalysisTable={AnalysisTable}
          TechView={props.Context === "Admin"}
          {...props}
        />
        <TaskListsPanel TaskListsTable={TaskListsTable} />
        {props.Context === "Admin" ? (
          <div className="SortAndFilters">
            <button
              onClick={(o) => {
                Toggle("SortTimeDescending");
              }}
              id="TimeToggle"
              title={
                Toggles.SortTimeDescending
                  ? "Order from New to Old"
                  : "Order from Old to New"
              }
            >
              {Toggles.SortTimeDescending === false ? (
                <React.Fragment>&#128338; &#9660;</React.Fragment>
              ) : (
                <React.Fragment>&#128338; &#9650;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowAccessLocks");
              }}
              id="ShowAccess"
              title={
                Toggles.ShowAccessLocks
                  ? "Hide Access Timeline"
                  : "Show Access Timeline"
              }
              className={Toggles.ShowAccessLocks ? "Active" : "Inactive"}
            >
              {Toggles.ShowAccessLocks === false ? (
                <React.Fragment>&#128065;</React.Fragment>
              ) : (
                <React.Fragment>&#128065;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowMail");
              }}
              id="ShowEmails"
              title={Toggles.ShowMail ? "Hide Emails" : "Show Emails"}
              className={Toggles.ShowMail ? "Active" : "Inactive"}
            >
              {Toggles.ShowMail === false ? (
                <React.Fragment>&#9993;</React.Fragment>
              ) : (
                <React.Fragment>&#9993;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowData");
              }}
              id="ShowData"
              title={Toggles.ShowData ? "Hide Data" : "Show Data"}
              className={Toggles.ShowData ? "Active" : "Inactive"}
            >
              {Toggles.ShowData === false ? (
                <React.Fragment>&#128218;</React.Fragment>
              ) : (
                <React.Fragment>&#128218;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowAssignments");
              }}
              id="ShowAssignments"
              title={
                Toggles.ShowAssignments
                  ? "Hide Assignments"
                  : "Show Assignments"
              }
              className={Toggles.ShowAssignments ? "Active" : "Inactive"}
            >
              {Toggles.ShowAssignments === false ? (
                <React.Fragment>&#128101;</React.Fragment>
              ) : (
                <React.Fragment>&#128101;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowEvents");
              }}
              id="ShowEvents"
              title={Toggles.ShowEvents ? "Hide Events" : "Show Events"}
              className={Toggles.ShowEvents ? "Active" : "Inactive"}
            >
              {Toggles.ShowEvents === false ? (
                <React.Fragment>&#127881;</React.Fragment>
              ) : (
                <React.Fragment>&#127881;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowDesignations");
              }}
              id="ShowDesignations"
              title={
                Toggles.ShowDesignations
                  ? "Hide Event Scans"
                  : "Show Event Scans"
              }
              className={Toggles.ShowDesignations ? "Active" : "Inactive"}
            >
              {Toggles.ShowDesignations === false ? (
                <React.Fragment>&#128269;</React.Fragment>
              ) : (
                <React.Fragment>&#128269;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowOther");
              }}
              id="ShowOther"
              title={
                Toggles.ShowOther
                  ? "Hide Lifecycle Events"
                  : "Show Lifecycle Events"
              }
              className={Toggles.ShowOther ? "Active" : "Inactive"}
            >
              {Toggles.ShowOther === false ? (
                <React.Fragment>&#127874;</React.Fragment>
              ) : (
                <React.Fragment>&#127874;</React.Fragment>
              )}
            </button>
            <button
              onClick={(o) => {
                Toggle("ShowMilliseconds");
              }}
              id="ShowMillisecondsToggle"
              title={
                Toggles.ShowMilliseconds
                  ? "Show Milliseconds"
                  : "Hide Milliseconds"
              }
              className={Toggles.ShowMilliseconds ? "Active" : "Inactive"}
            >
              .000
            </button>
            <button
              className="Reevaluator"
              onClick={(o) => {
                Designate();
              }}
            >
              Reevaluate Events
            </button>
          </div>
        ) : (
          ""
        )}

        <Timeline
          FIGUID={props.figuid}
          DA={props.da}
          EventTable={EventTable}
          DesignationTable={DesignationTable}
          AccessTable={AccessTable}
          CommitTable={CommitTable}
          CommitDatumsTable={CommitDatumsTable}
          AssignmentHistoryTable={AssignmentHistoryTable}
          OtherTable={OtherTable}
          MailTable={MailTable}
          Toggles={Toggles}
          TechView={props.Context === "Admin"}
          PassthroughRequeue={PassthroughRequeue}
        />

        {props.Context === "Admin" ? (
          <div className="DiagnosticArea">
            <h3>Admin Panel</h3>
            <hr />
            <p>
              Force reassign this form by typing in a valid assignee (username
              or collagen group id) below and clicking 'Force Reassign'.
            </p>
            <p>
              The new assignment will take a few seconds to load in at the top
              of the page.
            </p>
            <select
              id="Reassignee_DD"
              className="Reassigner"
              onChange={(ev) => {
                SetProvisionalReassignGroup(ev.target.value.toString());
              }}
            >
              <option value="0">Single User</option>
              {RelevantGroups.map((th) => {
                return (
                  <option
                    value={th.GroupID.toString()}
                    selected={ProvisionalReassignGroup == th.GroupID.toString()}
                  >
                    {th.GroupName} - [{th.GroupID.toString()}]
                  </option>
                );
              })}
            </select>
            <input
              placeholder="username"
              id="Reassignee"
              type="text"
              className="Reassigner"
              value={ProvisionalReassign}
              disabled={ProvisionalReassignGroup !== "0"}
              onChange={(ev) => {
                SetProvisionalReassign(ev.target.value);
              }}
            ></input>
            <button
              className="Reassigner"
              id="ReassignButton"
              disabled={
                ProvisionalReassign === "" && ProvisionalReassignGroup == ""
              }
              onClick={(o) => {
                ForceReassign();
              }}
            >
              Force Reassign
            </button>
            <hr />
            <p>
              Diagnose why an event isn't running by picking an event below and
              clicking 'Diagnose'.
            </p>
            <p>
              Force run a (non-reassignment) event by picking an event below and
              clicking 'Force Run'.
            </p>
            <div id="DiagnosisInputs">
              <label htmlFor="RequestedDiagnosisSelector">
                Diagnose Event:{" "}
              </label>
              <select
                id="RequestedDiagnosisSelector"
                onChange={(ev) => {
                  SetDiagnoseThis(ev.target.value);
                }}
              >
                <option value="0">-</option>
                {DiagnosisRequestOptions.map((th, i) => {
                  return (
                    <option key={"dro_" + i.toString()} value={th.Value}>
                      {th.Name}
                    </option>
                  );
                })}
                ;
              </select>
            </div>
            <div id="DiagnosisButtons">
              <IfPermitted Whitelist={["Collagen.Admins", "Admin.WEBUI"]}>
                <button
                  className="DiagnoseButton"
                  onClick={(ev) => {
                    SetForcedEventID(Number.parseInt(DiagnoseThis));
                  }}
                >
                  Force Run
                </button>
              </IfPermitted>
              <button
                className="DiagnoseButton"
                onClick={(ev) => {
                  SetRequestedDiagnosis(Number.parseInt(DiagnoseThis));
                }}
              >
                Diagnose
              </button>
            </div>
            <hr />
            <DiagnosticPanel
              Diagnosis={DiagnosisObject}
              SprocDataTable={SprocDataTable}
              InstanceDataTable={InstanceDataTable}
            />
          </div>
        ) : (
          ""
        )}
      </div>
    );
  } else {
    return <div className="error-message">We're in an unknown state.</div>;
  }
}
